import { useCallback, useRef, useState } from 'react'

import { useBankAccountListData } from '@features/bankAccounts/hooks/useBankAccountListData'
import { usePollBankAccountByBookkeepingId } from '@features/bankAccounts/hooks/usePollBankAccountByBookkeepingId'
import { updateBankAccount } from '@features/bankAccounts/query-api'
import { BankAccount } from '@features/bankAccounts/types/bankAccount'
import { Account } from '@modules-deprecated/app/accounts/types'
import { useUserOrganization } from '@modules-deprecated/app/organization'

import { queryClient } from '../../../config/queryClient'
import { CustomEvent } from '../../../enums/customEvent'
import { QueryKeys } from '../../../enums/queryKeys'
import { formatDate } from '../../../utils'
import { dispatchCustomEvent } from '../../../utils/customEvent'
import { SaveAccountPayload, UpdateAccountResponse } from '../query-api'
import { useCreateBookkeepingBankAccount } from './useCreateBookkeepingBankAccount'
import { useUpdateExistingAccount } from './useUpdateExistingAccount'

interface UseSaveBankAccountProps {
  onError?: () => void
  onSuccess?: (account: Account) => void
}

export interface SaveBankAccountPayload {
  id?: string
  name: string
  description: string
  currencyId: string
  bankAccountId?: string
  reconciliationStartDate?: Date | null
}

const mapToAccountPayload = (accountId: string, payload: SaveBankAccountPayload): Partial<SaveAccountPayload> => {
  return {
    id: accountId,
    name: payload.name,
    description: payload.description,
    currencyId: payload.currencyId,
  }
}

export const useSaveBankAccount = ({ onError, onSuccess }: UseSaveBankAccountProps = {}) => {
  const [isProcessing, setIsProcessing] = useState<boolean>(false)
  const currentPayloadRef = useRef<SaveBankAccountPayload | null>(null)
  const [pendingBookkeepingAccount, setPendingBookkeepingAccount] = useState<Account | null>(null)
  const { organization } = useUserOrganization()
  const { activeAccounts, bankAccountsByAccountId, hasOnlyEmptyDefaultBankAccount } = useBankAccountListData()

  const handleSaveSuccess = useCallback(
    async (account: Account) => {
      await Promise.all([
        queryClient.invalidateQueries([QueryKeys.Accounts, organization?.id]),
        queryClient.invalidateQueries(QueryKeys.BankAccounts),
      ])
      setIsProcessing(false)
      onSuccess?.(account)
    },
    [onSuccess, organization],
  )

  const handleSaveFailure = useCallback(() => {
    setIsProcessing(false)
    onError?.()
  }, [onError])

  const { createBookkeepingBankAccount: createBankAccount } = useCreateBookkeepingBankAccount({
    onSuccess: (account: Account) => {
      const reconciliationStartDate = currentPayloadRef.current?.reconciliationStartDate

      if (reconciliationStartDate) {
        setPendingBookkeepingAccount(account)
      } else {
        handleSaveSuccess(account)
      }
    },
    onError: () => handleSaveFailure(),
  })

  const { updateExistingAccount: updateBookkeepingAccount } = useUpdateExistingAccount({
    onSuccess: async (response: UpdateAccountResponse) => {
      const account = response.accounts[0]
      const payload = currentPayloadRef.current

      if (payload && payload.bankAccountId) {
        await updateBankAccountData(account, payload.bankAccountId)
      }
    },
    onError: () => handleSaveFailure(),
  })

  usePollBankAccountByBookkeepingId({
    polling: !!pendingBookkeepingAccount,
    bookkeepingAccountId: pendingBookkeepingAccount?.id ?? null,
    onSuccess: async (bankAccount: BankAccount) => {
      if (!pendingBookkeepingAccount) {
        return
      }

      setPendingBookkeepingAccount(null)
      await updateBankAccountData(pendingBookkeepingAccount, bankAccount.id)
    },
    onTimeout: () => {
      setPendingBookkeepingAccount(null)
      currentPayloadRef.current = null
      handleSaveFailure()
    },
  })

  const updateExistingDefaultBankAccount = useCallback(
    (payload: SaveBankAccountPayload) => {
      const bookkeepingAccountId = activeAccounts?.[0]?.id ?? ''
      const updateExistingAccountPayload: Partial<SaveAccountPayload> = mapToAccountPayload(
        bookkeepingAccountId,
        payload,
      )
      const bankAccount = bankAccountsByAccountId.get(bookkeepingAccountId)

      if (!bankAccount) {
        handleSaveFailure()
        return
      }

      currentPayloadRef.current = {
        ...payload,
        id: bookkeepingAccountId,
        bankAccountId: bankAccount.id,
      }

      updateBookkeepingAccount(updateExistingAccountPayload)
    },
    [activeAccounts, bankAccountsByAccountId, updateBookkeepingAccount, handleSaveFailure],
  )

  const updateBankAccountData = useCallback(
    async (bookeepingBankAccount: Account, bankAccountId: string) => {
      const payload = currentPayloadRef.current

      if (payload && organization?.id) {
        currentPayloadRef.current = null
        try {
          await updateBankAccount({
            organizationId: organization.id,
            bankAccountId,
            reconciliationStartDate:
              payload.reconciliationStartDate === undefined
                ? undefined
                : (formatDate(payload.reconciliationStartDate) ?? null),
          })

          if (payload.reconciliationStartDate !== undefined) {
            dispatchCustomEvent(CustomEvent.BankAccountReconciliationStartDateUpdated)
          }
        } catch {
          handleSaveFailure()
          return
        }
      }

      handleSaveSuccess(bookeepingBankAccount)
    },
    [handleSaveFailure, handleSaveSuccess, organization],
  )

  const saveBankAccount = useCallback(
    (payload: SaveBankAccountPayload) => {
      setIsProcessing(true)
      currentPayloadRef.current = payload

      if (payload.id) {
        updateBookkeepingAccount(mapToAccountPayload(payload.id, payload))
      } else if (hasOnlyEmptyDefaultBankAccount) {
        updateExistingDefaultBankAccount(payload)
      } else {
        createBankAccount({
          organizationId: organization?.id ?? '',
          newBankAccountPayload: {
            name: payload.name,
            description: payload.description,
            currencyId: payload.currencyId,
          },
        })
      }
    },
    [
      organization,
      createBankAccount,
      updateBookkeepingAccount,
      updateExistingDefaultBankAccount,
      hasOnlyEmptyDefaultBankAccount,
    ],
  )

  return { saveBankAccount, isProcessing }
}
